import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FileText,
  MessageCircle,
  Mic,
  Paperclip,
  Phone,
  PhoneIncoming,
  PhoneOff,
  Send,
  Square,
  Video,
  X,
} from 'lucide-react';
import { useTranslation } from '@most/i18n';
import { Button, cn } from '@most/ui';
import { useSupportCall } from '@most/webrtc-call';
import type { ChatMessage } from '@most/api-types';
import {
  chatCallApi,
  useChatConversation,
  useChatMessages,
  useMarkChatRead,
  useSendChatMessage,
  useUploadChatAttachment,
  type ChatUploadKind,
} from '../../api/chat';
import { chatEvents } from '../../api/chatEvents';
import { AudioMessage } from './AudioMessage';

/**
 * The floating support-chat widget (bottom-right, every buyer screen).
 *
 * The other party is always "support" — the API never says who answers, and neither does
 * this UI. Message direction is the `from_admin` bit; that is the entire sender model.
 */
export function SupportChat() {
  const { t, i18n } = useTranslation('buyer');
  const [open, setOpen] = useState(false);

  const conversation = useChatConversation();
  const messagesQuery = useChatMessages(open);
  const send = useSendChatMessage();
  const upload = useUploadChatAttachment();
  const markRead = useMarkChatRead();

  const call = useSupportCall({
    api: chatCallApi,
    side: 'user',
    onTerminal: () => messagesQuery.refetch(),
  });

  // The app's single socket forwards chat.*/call.* nudges through chatEvents.
  useEffect(
    () =>
      chatEvents.subscribe((e) => {
        if (!e.id) return;
        if (e.type === 'call.state') call.onCallStateFrame(e.id);
        if (e.type === 'call.signal') call.onCallSignalFrame(e.id);
      }),
    [call.onCallStateFrame, call.onCallSignalFrame], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const unread = conversation.data?.unread_count ?? 0;

  // Opening the panel (or new support messages arriving while it is open) marks them read.
  useEffect(() => {
    if (open && unread > 0 && !markRead.isPending) {
      markRead.mutate();
    }
  }, [open, unread]); // eslint-disable-line react-hooks/exhaustive-deps

  const messages = useMemo(() => {
    const pages = messagesQuery.data?.pages ?? [];
    // Server pages newest-first; the thread renders oldest-first.
    return pages.flatMap((p) => p.items).reverse();
  }, [messagesQuery.data]);

  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [open, messages.length]);

  const [draft, setDraft] = useState('');
  const submitText = useCallback(() => {
    const body = draft.trim();
    if (!body || send.isPending) return;
    send.mutate(body, { onSuccess: () => setDraft('') });
  }, [draft, send]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const onPickFile = useCallback(
    (file: File) => {
      const kind: ChatUploadKind = file.type.startsWith('image/')
        ? 'image'
        : file.type.startsWith('video/')
          ? 'video'
          : file.type.startsWith('audio/')
            ? 'audio'
            : 'file';
      upload.mutate({ kind, file });
    },
    [upload],
  );

  // ── Voice messages (MediaRecorder, hidden where unsupported) ──────────────
  const [recording, setRecording] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const canRecord = typeof window !== 'undefined' && 'MediaRecorder' in window;

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      const rec = new MediaRecorder(stream, { mimeType: mime });
      const chunks: BlobPart[] = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = () => {
        stream.getTracks().forEach((tr) => tr.stop());
        const blob = new Blob(chunks, { type: mime });
        if (blob.size > 0) upload.mutate({ kind: 'audio', file: blob });
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      // Mic denied — the button simply does nothing further.
    }
  }, [upload]);

  const stopRecording = useCallback(() => {
    recorderRef.current?.stop();
    recorderRef.current = null;
    setRecording(false);
  }, []);

  const timeFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { hour: '2-digit', minute: '2-digit' }),
    [i18n.language],
  );

  return (
    <>
      {/* The launcher */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? t('chat.close') : t('chat.open')}
        className={cn(
          'fixed bottom-4 right-4 z-50 flex h-14 w-14 items-center justify-center rounded-full',
          'bg-slate-900 text-white shadow-xl transition hover:scale-105 focus-visible:outline focus-visible:outline-2',
        )}
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
        {!open && unread > 0 && (
          <span className="absolute -right-1 -top-1 flex h-6 min-w-6 items-center justify-center rounded-full bg-red-500 px-1 text-xs font-bold text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {/* The panel */}
      {open && (
        <div
          role="dialog"
          aria-label={t('chat.title')}
          className="fixed bottom-24 right-4 z-50 flex h-[560px] max-h-[calc(100dvh-7rem)] w-[380px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
        >
          <header className="flex items-center gap-2 border-b border-slate-200 bg-slate-900 px-4 py-3 text-white">
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold">{t('chat.title')}</div>
              <div className="truncate text-xs text-slate-300">{t('chat.subtitle')}</div>
            </div>
            <Button
              variant="ghost"
              size="icon-sm"
              className="text-white hover:bg-white/10"
              aria-label={t('chat.call.audio')}
              disabled={call.phase !== 'idle'}
              onClick={() => void call.startCall('audio')}
            >
              <Phone className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              className="text-white hover:bg-white/10"
              aria-label={t('chat.call.video')}
              disabled={call.phase !== 'idle'}
              onClick={() => void call.startCall('video')}
            >
              <Video className="h-4 w-4" />
            </Button>
          </header>

          <div ref={listRef} className="flex-1 space-y-2 overflow-y-auto bg-slate-50 p-3">
            {messagesQuery.hasNextPage && (
              <div className="text-center">
                <Button
                  variant="ghost"
                  size="xs"
                  disabled={messagesQuery.isFetchingNextPage}
                  onClick={() => void messagesQuery.fetchNextPage()}
                >
                  {t('chat.loadOlder')}
                </Button>
              </div>
            )}
            {messages.length === 0 && !messagesQuery.isLoading && (
              <p className="px-4 py-8 text-center text-sm text-slate-500">{t('chat.empty')}</p>
            )}
            {messages.map((m) => (
              <MessageBubble key={m.id} message={m} timeFmt={timeFmt} t={t} />
            ))}
          </div>

          <footer className="border-t border-slate-200 bg-white p-2">
            <div className="flex items-end gap-1">
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept="image/jpeg,image/png,image/webp,video/mp4,video/webm,audio/mpeg,audio/ogg,audio/webm,audio/mp4,application/pdf,application/zip,.docx,.xlsx"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onPickFile(f);
                  e.target.value = '';
                }}
              />
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t('chat.attach')}
                disabled={upload.isPending}
                onClick={() => fileInputRef.current?.click()}
              >
                <Paperclip className="h-4 w-4" />
              </Button>
              {canRecord && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={recording ? t('chat.stopRecording') : t('chat.record')}
                  className={cn(recording && 'text-red-600')}
                  onClick={() => (recording ? stopRecording() : void startRecording())}
                >
                  {recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                </Button>
              )}
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    submitText();
                  }
                }}
                rows={1}
                placeholder={t('chat.placeholder')}
                className="max-h-28 min-h-10 flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus-visible:border-slate-400 focus-visible:ring-1 focus-visible:ring-slate-300 focus-visible:ring-offset-0"
              />
              <Button
                size="icon-sm"
                aria-label={t('chat.send')}
                disabled={!draft.trim() || send.isPending}
                onClick={submitText}
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            {(send.isError || upload.isError) && (
              <p className="px-2 pt-1 text-xs text-red-600">{t('chat.sendError')}</p>
            )}
          </footer>
        </div>
      )}

      <CallOverlay call={call} t={t} />
    </>
  );
}

type Translate = ReturnType<typeof useTranslation>['t'];

function MessageBubble({
  message: m,
  timeFmt,
  t,
}: {
  message: ChatMessage;
  timeFmt: Intl.DateTimeFormat;
  t: Translate;
}) {
  const mine = !m.from_admin;
  return (
    <div className={cn('flex', mine ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm',
          mine ? 'rounded-br-sm bg-slate-900 text-white' : 'rounded-bl-sm border border-slate-200 bg-white text-slate-900',
        )}
      >
        <MessageContent message={m} mine={mine} t={t} />
        <div className={cn('mt-1 text-right text-[10px]', mine ? 'text-slate-400' : 'text-slate-400')}>
          {timeFmt.format(new Date(m.created_at))}
          {mine && m.read_at ? ' ✓✓' : mine ? ' ✓' : ''}
        </div>
      </div>
    </div>
  );
}

function MessageContent({ message: m, mine, t }: { message: ChatMessage; mine: boolean; t: Translate }) {
  if (m.kind === 'call' && m.call) {
    const dur =
      m.call.seconds != null
        ? ` · ${Math.floor(m.call.seconds / 60)}:${String(m.call.seconds % 60).padStart(2, '0')}`
        : '';
    return (
      <span className="flex items-center gap-1.5">
        {m.call.kind === 'video' ? <Video className="h-4 w-4" /> : <Phone className="h-4 w-4" />}
        {t(`chat.call.${m.call.outcome}` as never)}
        {dur}
      </span>
    );
  }

  const att = m.attachment;
  return (
    <>
      {m.kind === 'image' && att && (
        <a href={att.url} target="_blank" rel="noreferrer">
          <img src={att.thumb_url ?? att.url} alt={att.name} className="mb-1 max-h-48 rounded-lg" loading="lazy" />
        </a>
      )}
      {m.kind === 'video' && att && (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video src={att.url} controls preload="metadata" className="mb-1 max-h-48 w-full rounded-lg" />
      )}
      {m.kind === 'audio' && att && <AudioMessage src={att.url} mine={mine} className="mb-1" />}
      {m.kind === 'file' && att && (
        <a
          href={att.url}
          download={att.name}
          className={cn(
            'mb-1 flex items-center gap-2 rounded-lg border px-2 py-1.5',
            mine ? 'border-white/20' : 'border-slate-200',
          )}
        >
          <FileText className="h-4 w-4 shrink-0" />
          <span className="min-w-0">
            <span className="block truncate text-xs font-medium">{att.name}</span>
            <span className={cn('block text-[10px]', mine ? 'text-slate-400' : 'text-slate-500')}>
              {formatSize(att.size)}
            </span>
          </span>
        </a>
      )}
      {m.body && <span className="whitespace-pre-wrap break-words">{m.body}</span>}
    </>
  );
}

function formatSize(bytes: number): string {
  if (bytes >= 1 << 20) return `${(bytes / (1 << 20)).toFixed(1)} MB`;
  if (bytes >= 1 << 10) return `${Math.round(bytes / (1 << 10))} KB`;
  return `${bytes} B`;
}

function CallOverlay({ call, t }: { call: ReturnType<typeof useSupportCall>; t: Translate }) {
  const remoteRef = useRef<HTMLVideoElement>(null);
  const localRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (remoteRef.current) remoteRef.current.srcObject = call.remoteStream;
  }, [call.remoteStream, call.phase]);
  useEffect(() => {
    if (localRef.current) localRef.current.srcObject = call.localStream;
  }, [call.localStream, call.phase]);

  if (call.phase === 'idle') return null;

  const isVideo = call.call?.kind === 'video';
  const statusKey =
    call.phase === 'outgoing'
      ? 'chat.call.outgoing'
      : call.phase === 'incoming'
        ? 'chat.call.incoming'
        : call.phase === 'connecting'
          ? 'chat.call.connecting'
          : call.phase === 'active'
            ? 'chat.call.active'
            : 'chat.call.endedStatus';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4">
      <div className="flex w-full max-w-md flex-col items-center gap-4 rounded-2xl bg-slate-900 p-6 text-white shadow-2xl">
        <p className="flex items-center gap-2 text-sm text-slate-300">
          {call.phase === 'incoming' ? (
            <PhoneIncoming className="h-4 w-4 animate-pulse text-green-400" />
          ) : isVideo ? (
            <Video className="h-4 w-4" />
          ) : (
            <Phone className="h-4 w-4" />
          )}
          {t(statusKey as never)}
        </p>

        <div className="relative w-full">
          {/* Remote media. A hidden video element still plays audio for audio-only calls. */}
          <video
            ref={remoteRef}
            autoPlay
            playsInline
            className={cn('w-full rounded-xl bg-black', isVideo ? 'aspect-video' : 'h-24 opacity-40')}
          />
          {isVideo && (
            <video
              ref={localRef}
              autoPlay
              playsInline
              muted
              className="absolute bottom-2 right-2 w-24 rounded-lg border border-white/30 bg-black"
            />
          )}
        </div>

        {call.error && <p className="text-xs text-red-400">{t(`chat.call.${errorKey(call.error)}` as never)}</p>}

        <div className="flex items-center gap-3">
          {call.phase === 'incoming' ? (
            <>
              <Button className="bg-green-600 hover:bg-green-500" onClick={() => void call.accept()}>
                <Phone className="mr-2 h-4 w-4" /> {t('chat.call.accept')}
              </Button>
              <Button variant="destructive" onClick={() => void call.decline()}>
                <PhoneOff className="mr-2 h-4 w-4" /> {t('chat.call.decline')}
              </Button>
            </>
          ) : (
            <Button variant="destructive" onClick={() => void call.hangUp()}>
              <PhoneOff className="mr-2 h-4 w-4" /> {t('chat.call.hangUp')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function errorKey(err: string): string {
  switch (err) {
    case 'media_denied':
      return 'mediaDenied';
    case 'connection_lost':
      return 'connectionLost';
    default:
      return 'failed';
  }
}
